import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Flame, RefreshCw, SquareKanban, Trophy, Wallet } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PageContainer } from '../../../common/components/PageContainer';
import { DeskList, LeadDeskStage, LeadTier, useLeadDeskApi } from '../../../common/hooks/use-lead-desk-api.hook';
import { EmptyState, PillTabs, SearchField, formatCurrency, formatNumber } from '../../../components/design-system/admin';
import { DeskBoard } from '../../../modules/vendor/lead-desk/components/DeskBoard';
import { columnsOf, moveCard } from '../../../modules/vendor/lead-desk/lead-desk-view';

type TierFilter = 'all' | LeadTier;
type AudienceFilter = 'all' | 'consumer' | 'trade';
const TIERS: TierFilter[] = ['all', 'hot', 'warm', 'cold'];
const AUDIENCES: AudienceFilter[] = ['all', 'consumer', 'trade'];

/** One number of the KPI line: an icon, the figure, what it is. */
function Kpi({ icon, label, value, hint, testId }: { icon: React.ReactNode; label: string; value: string; hint?: string; testId?: string }) {
    return (
        <div className="flex min-w-0 items-center gap-3 px-4 py-3" data-testid={testId}>
            <span className="text-muted-foreground [&_svg]:size-4">{icon}</span>
            <div className="min-w-0">
                <p className="truncate text-xs text-muted-foreground">{label}</p>
                <p className="truncate">
                    <span className="font-heading text-lg font-medium tabular-nums">{value}</span>
                    {hint && <span className="ms-2 text-xs text-muted-foreground">{hint}</span>}
                </p>
            </div>
        </div>
    );
}

/**
 * The lead desk: every lead on one pipeline, from the first room downloaded
 * to the order won — hottest first in each column, dragged from stage to
 * stage, opened as a file with a click.
 */
export const LeadDeskPage = () => {
    const { t } = useTranslation();
    const api = useLeadDeskApi();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const tier: TierFilter = TIERS.includes(params.get('tier') as TierFilter) ? (params.get('tier') as TierFilter) : 'all';
    const audience: AudienceFilter = AUDIENCES.includes(params.get('audience') as AudienceFilter) ? (params.get('audience') as AudienceFilter) : 'all';
    const [search, setSearch] = useState(params.get('q') ?? '');
    const [data, setData] = useState<DeskList | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [moveError, setMoveError] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            setData(await api.list({ limit: 500 }));
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        load();
    }, [load]);

    const setParam = (key: string, value: string) => {
        const next = new URLSearchParams(params);
        if (value === 'all' || !value) next.delete(key);
        else next.set(key, value);
        setParams(next, { replace: true });
    };

    // Filtered here, not by the API: the KPI line and the column counts read the whole desk.
    const cards = useMemo(() => {
        const q = search.trim().toLowerCase();
        return (data?.data ?? [])
            .filter((c) => tier === 'all' || c.tier === tier)
            .filter((c) => audience === 'all' || c.audience === audience)
            .filter((c) => !q
                || c.email.includes(q)
                || (c.name ?? '').toLowerCase().includes(q)
                || (c.phone ?? '').replace(/\s/g, '').includes(q.replace(/\s/g, ''))
                || c.products.some((p) => p.title.toLowerCase().includes(q)));
    }, [data, tier, audience, search]);
    const columns = useMemo(() => columnsOf(cards), [cards]);

    const onMove = async (id: string, stage: LeadDeskStage) => {
        if (!data) return;
        const before = data;
        setMoveError(false);
        setData({ ...data, data: moveCard(data.data, id, stage) });
        try {
            await api.patchDesk(id, { stage });
        } catch {
            setData(before);
            setMoveError(true);
        }
    };

    const kpis = data?.kpis;
    const counts = data?.counts;
    const noLeads = !loading && !error && data && data.data.length === 0;

    return (
        <PageContainer breadcrumbs={[{ label: t('R2R.WORKSPACE.SALES') }, { label: t('R2R.NAV.DESK') }]}>
            <div className="mx-auto flex w-full min-w-0 max-w-[1600px] flex-col gap-5" data-testid="lead-desk">
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <div className="min-w-0">
                        <h1 className="font-heading text-[22px] font-medium tracking-tight">{t('LEAD_DESK.TITLE')}</h1>
                        <p className="mt-1 text-sm text-muted-foreground">{t('LEAD_DESK.SUBTITLE')}</p>
                    </div>
                    <Button variant="outline" size="sm" className="h-8" onClick={load} disabled={loading} aria-label={t('LEAD_DESK.REFRESH')}>
                        <RefreshCw className={cn(loading && 'animate-spin')} />
                    </Button>
                </div>

                {/* The KPI line: what is waiting, what is in play, what came in. */}
                <Card className="gap-0 py-0">
                    <CardContent className="grid grid-cols-1 divide-y px-0 sm:grid-cols-3 sm:divide-x sm:divide-y-0 rtl:sm:divide-x-reverse">
                        <Kpi
                            testId="desk-kpi-hot"
                            icon={<Flame />}
                            label={t('LEAD_DESK.KPI.HOT_WAITING')}
                            value={loading && !kpis ? '…' : formatNumber(kpis?.hotWaiting ?? 0)}
                            hint={t('LEAD_DESK.KPI.HOT_WAITING_HINT')}
                        />
                        <Kpi
                            testId="desk-kpi-pipeline"
                            icon={<Wallet />}
                            label={t('LEAD_DESK.KPI.PIPELINE')}
                            value={kpis?.pipelineValue ? formatCurrency(kpis.pipelineValue.amount, kpis.pipelineValue.currency, 0) : loading ? '…' : t('LEAD_DESK.KPI.NONE')}
                            hint={kpis?.pipelineValue ? t('LEAD_DESK.KPI.PIPELINE_HINT', { n: kpis.pipelineValue.leads }) : undefined}
                        />
                        <Kpi
                            testId="desk-kpi-won"
                            icon={<Trophy />}
                            label={t('LEAD_DESK.KPI.WON_MONTH')}
                            value={kpis?.wonThisMonth.amount != null && kpis.wonThisMonth.currency
                                ? formatCurrency(kpis.wonThisMonth.amount, kpis.wonThisMonth.currency, 0)
                                : loading ? '…' : formatNumber(kpis?.wonThisMonth.count ?? 0)}
                            hint={kpis ? t('LEAD_DESK.KPI.WON_MONTH_HINT', { n: kpis.wonThisMonth.count }) : undefined}
                        />
                    </CardContent>
                </Card>

                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                        <PillTabs<TierFilter>
                            value={tier}
                            onChange={(v) => setParam('tier', v)}
                            ariaLabel={t('LEAD_DESK.FILTER.TIER')}
                            options={TIERS.map((v) => ({ value: v, label: t(`LEAD_DESK.FILTER.${v.toUpperCase()}`), count: v === 'all' ? undefined : counts?.byTier[v] }))}
                        />
                        <PillTabs<AudienceFilter>
                            value={audience}
                            onChange={(v) => setParam('audience', v)}
                            ariaLabel={t('LEAD_DESK.FILTER.AUDIENCE')}
                            options={AUDIENCES.map((v) => ({ value: v, label: t(`LEAD_DESK.FILTER.${v.toUpperCase()}`), count: v === 'all' ? undefined : counts?.byAudience[v] }))}
                        />
                    </div>
                    <SearchField
                        value={search}
                        onChange={(v) => {
                            setSearch(v);
                            setParam('q', v);
                        }}
                        placeholder={t('LEAD_DESK.SEARCH')}
                    />
                </div>

                {moveError && <p className="text-sm text-destructive" role="alert">{t('LEAD_DESK.MOVE_FAILED')}</p>}

                {error && (
                    <Card>
                        <CardContent className="flex flex-wrap items-center justify-between gap-3">
                            <p className="text-sm text-muted-foreground">{t('LEAD_DESK.LOAD_FAILED')}</p>
                            <Button variant="outline" size="sm" onClick={load}>{t('LEAD_DESK.RETRY')}</Button>
                        </CardContent>
                    </Card>
                )}

                {loading && !data && (
                    <div className="flex gap-3 overflow-hidden">
                        {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-[420px] w-[272px] shrink-0 rounded-xl" />)}
                    </div>
                )}

                {noLeads && (
                    <Card className="py-2">
                        <EmptyState icon={<SquareKanban />} title={t('LEAD_DESK.EMPTY_TITLE')} description={t('LEAD_DESK.EMPTY_BODY')} />
                    </Card>
                )}

                {data && data.data.length > 0 && (
                    <>
                        <DeskBoard columns={columns} onMove={onMove} onOpen={(id) => navigate(`/sales/leads/${id}`)} />
                        <p className="text-xs text-muted-foreground">{t('LEAD_DESK.DRAG_HINT')}</p>
                    </>
                )}

                {data && data.lockedCount > 0 && (
                    <p className="text-sm text-muted-foreground" data-testid="desk-locked">
                        {t('LEAD_DESK.LOCKED', { n: data.lockedCount })}{' '}
                        <Link to="/home/billing/plan" className="text-foreground underline underline-offset-4">{t('LEAD_DESK.SEE_PLANS')}</Link>
                    </p>
                )}
            </div>
        </PageContainer>
    );
};

export default LeadDeskPage;
